
import { Component, Emit, Mixins, Watch } from "vue-property-decorator";
import { validationMixin } from "vuelidate";
import { namespace, Getter } from "vuex-class";
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import vselect3 from "vselect3";
import { maxLength, minValue, required, requiredIf } from "vuelidate/lib/validators";
import { isEqualWith, get } from "lodash";

import { ServerModelDetails } from "@/components/device-setup/ServerModal.vue";
import { HealthCheckOption, ServerDetails, ServerLineProfile, ServerType } from "@/store/devices/types";
import AreaTreeSelect from "@/components/form/AreaTreeSelect.vue";
import ApplianceList from "@/components/devices/ApplianceList.vue";
import { FeaturesList, UserPermissions } from "@/store/types";
import { serviceLevelAgreementMixins } from '@/mixins';
import CustomDefinedFieldInput from '@/components/custom-fields/CustomDefinedFieldInput.vue'
import { CustomFieldTableTypes, CustomFieldValues, CustomFieldDto } from "@/store/custom-fields/types";
import { hasEmptyRequiredCustomFields, hasInvalidCustomFieldValues } from "@/utils/CustomFieldLogic";
import { devicesApi } from "@/services/api.devices.service";
import { debounce } from "lodash";
import { getDebouncePeriod } from '@/utils/debounce-helper';
import { Constants } from "@/types/Constants";
import { numberFormattingMixins } from '@/mixins';
import { HealthCheckTypeIds } from "@/types/sv-data/OrganizationSettings";
import organizationApi from "@/services/api.organizationSettings";

const Devices = namespace("devices");
const CustomFields = namespace("customFields");

const AlarmGroupingNone: string = "No Grouping";
const AlarmGroupingResponse: string = "By Response";
const AlarmGroupingDefault: string = "By Area";

const bannedNumberFieldKeys: string[] = ["e", "+", "-"];

const { validateSlaBreachSecondsValue, generateErrorMessage } = serviceLevelAgreementMixins.methods;

const { enforceWholeNumber, numberFieldBadDataProtection } = numberFormattingMixins.methods;

@Component({
	components: {
		"area-tree-select": AreaTreeSelect,
		"v-select-3": vselect3,
		"vue-perfect-scrollbar": VuePerfectScrollbar,
		"appliance-list": ApplianceList,
		"custom-defined-field-input": CustomDefinedFieldInput
	},
	validations: {
		selectedServer: {
			groupID: { required, minValue: minValue(1) },
			serverTypeID: { required, minValue: minValue(1) },
			title: { required },
			applianceId: { required: requiredIf(function () {
				return this.isApplianceOnly;
			})},
			accountNumber: { maxLength: maxLength(20) },
		},
		hasDataChanged: {
			required: requiredIf(function () {
				const selectedServerWithValidationFieldsOnly = {
					...this.selectedServer,
					deviceTypeCounts: null,
				};

				const storeServerWithValidationFieldsOnly = {
					...this.currentServer,
					deviceTypeCounts: null,
				};

				return isEqualWith(
					selectedServerWithValidationFieldsOnly,
					storeServerWithValidationFieldsOnly,
					(propFromSelectedServer, propFromStoreServer) => {
						if (
							(propFromSelectedServer === null || propFromSelectedServer === "") &&
							(propFromStoreServer === null || propFromStoreServer === "")
						) {
							return true;
						}
					}
				);
			}),
		},
	},
})
export default class DeviceEdit extends Mixins(validationMixin) {
	@Getter("getFeaturesList") private featuresList: FeaturesList;
	@Getter("getPermissions") private permissions: UserPermissions;
	@Devices.Action private getDeviceTypeCounts: (serverId: number | null) => Promise<void>;
	@Devices.State private currentServer: ServerDetails;
	@Devices.State private serverLineProfiles: ServerLineProfile[];
	@Devices.State private serverTypes: ServerType[];
	@Devices.Mutation private setExtraValue: (extraValue: string) => void;
	@Devices.Getter("getSortedServerTypes") private sortedServerTypes: ServerType[];
	@Getter("getMaxResponseSlaDuration") maxResponseSlaDuration: number;

	@CustomFields.State private serverCustomFields: CustomFieldDto[];

	private alarmGrouping: string = AlarmGroupingDefault;
	private selectedServer: ServerDetails = null;
	private deviceEditLoaded: boolean = false;
	private areaSelectLoaded: boolean = false;
	private readonly customFieldTableType: number = CustomFieldTableTypes.Server;
	private customFieldValues: CustomFieldValues[] = [];
	private maxIntervalValue: number = 35791394;
	private maxTimeoutValue: number = Constants.MAXIMUM_INTEGER_VALUE;
	private healthCheckType: number | null = null;

	private heartbeatOptions: HealthCheckOption[] = [
		{ value: null, label: 'Not Set' },
		{ value: false, label: 'Disabled' },
		{ value: true, label: 'Enabled' }
	]

	private async created(): Promise<void> {
		this.selectedServer = { ...this.currentServer };
		this.customFieldValues = this.selectedServer.customFields;

		if (this.isEditing) {
			this.setAlarmGroupingOption();
			await this.getDeviceTypeCounts(this.selectedServer.serverID); // Triggers re-render, subsequent state changes may cause race condition
		}

		this.deviceEditLoaded = true;

		if (this.showHealthCheckSettings) {
			this.healthCheckType = await organizationApi.retrieveHealthCheckType();
		}
	}

	private updateExtraValue(ev) {
		this.setExtraValue(ev);
	}

	private setAlarmGroupingOption(): void {
		if (this.selectedServer.isRaiseIndividual) {
			this.alarmGrouping = AlarmGroupingNone;
		} else if (this.selectedServer.isRaiseGrouped) {
			this.alarmGrouping = AlarmGroupingResponse;
		} else {
			this.alarmGrouping = AlarmGroupingDefault;
		}
	}

	@Watch("componentLoaded")
	private onComponentLoaded(newValue: boolean, oldValue: boolean) {
		if (newValue && newValue != oldValue) {
			this.$emit("component-loaded");
		}
	}

	private get componentLoaded(): boolean {
		return this.deviceEditLoaded && this.areaSelectLoaded;
	}

	private get isEditing(): boolean {
		return this.selectedServer.serverID > 0;
	}

	private get alarmGroupingOptions(): string[] {
		return [AlarmGroupingNone, AlarmGroupingResponse, AlarmGroupingDefault];
	}

	private handleAlarmGroupingSelected(): void {
		switch (this.alarmGrouping) {
			case AlarmGroupingNone:
				this.selectedServer.isRaiseIndividual = true;
				this.selectedServer.isRaiseGrouped = false;
				break;
			case AlarmGroupingResponse:
				this.selectedServer.isRaiseIndividual = false;
				this.selectedServer.isRaiseGrouped = true;
				break;
			default:
				this.selectedServer.isRaiseIndividual = false;
				this.selectedServer.isRaiseGrouped = false;
		}
	}

	@Watch("selectedServer", { deep: true })
	@Watch("currentServer", { deep: true })
	@Emit("server-model-details-changed")
	private handleServerDetailsChanged(): ServerModelDetails {
		let invalid = this.$v.$invalid;

		if (this.isDeviceLevelFailoverEnabled) {
			invalid = this.$v.$invalid || !this.appliancesValid;

			if (this.selectedServer.applianceId != null && this.selectedServer.primaryApplianceId == null) {
				this.selectedServer.primaryApplianceId = this.selectedServer.applianceId;
			}
		}

		if (!this.isEventECareEnabled || !this.doesServerSupportECare &&
			(this.selectedServer.eventTestInterval != null || this.selectedServer.eventTestTimeout != null)){
			this.selectedServer.eventTestInterval = null;
			this.selectedServer.eventTestTimeout = null;
		}

		return {
			invalid: invalid || this.areManuallyValidatedFieldsInvalid(),
			selectedServer: this.selectedServer
		};
	}

	private areManuallyValidatedFieldsInvalid(): boolean {
		return !this.isSlaBreachSecondsValid || this.areCustomFieldValuesInvalid() || this.areEventHealthFieldsInvalid();
	}

	private get isSlaBreachSecondsValid(): boolean {
		// Only consider current value if feature enabled and editing permission enabled
		if (this.isSlaEnabled && this.permissions.canEditSLAs) {
			return validateSlaBreachSecondsValue(this.selectedServer.slaBreachSeconds, this.maxResponseSlaDuration);
		}

		return true;
	}

	private get slaBreachSecondsErrorMessage(): string {
		return generateErrorMessage(this.maxResponseSlaDuration);
	}

	/*
		INVALID CASES for Secondary Ops Link
		1. applianceId is not set and secondaryId is set
			- this.InvalidSecondaryAppliance
		2. applianceId is the same as the secondaryId BUT a fail over has NOT occurred (applianceId is the same as PrimaryApplianceId)
			- this.appliancesMatch && !this.applianceHasFailedOver
	*/

	private get appliancesValid(): boolean {
		return (!this.appliancesMatch && !this.InvalidSecondaryAppliance) || (this.appliancesMatch && this.applianceHasFailedOver) && !this.InvalidSecondaryAppliance;
	}

	// The ApplianceId and the SecondaryApplianceId are the same
	private get appliancesMatch(): boolean {
		return this.selectedServer?.applianceId != null && this.selectedServer?.applianceId == this.selectedServer?.secondaryApplianceId;
	}

	// The Appliance PrimaryApplianceId is different form the current ApplianceId
	private get applianceHasFailedOver(): boolean {
		return this.selectedServer?.applianceId != null &&
			this.selectedServer?.primaryApplianceId != null &&
			this.selectedServer?.applianceId != this.selectedServer?.primaryApplianceId;
	}

	// A SecondaryApplianceId has been set without an ApplianceId
	private get InvalidSecondaryAppliance(): boolean {
		return this.selectedServer?.applianceId == null && this.selectedServer?.secondaryApplianceId != null;
	}

	private get extraValueLabel(): string {
		return `${this.extraValuePlaceholder}:`;
	}

	private get extraValuePlaceholder(): string {
		const serverType = this.selectedServerType;

		if (serverType && serverType.extraValueName) {
			return serverType.extraValueName;
		}

		return "Extra Value";
	}

	private get selectedServerType(): ServerType {
		return this.serverTypes.find((st) => st.serverTypeID === this.selectedServer.serverTypeID);
	}

	private get serverTypeIsAlarmPanel(): boolean {
		const serverType = this.selectedServerType;

		if (serverType && serverType.isAlarmPanel) {
			return true;
		}

		return false;
	}

	private areaSelectComponentLoaded(): void {
		this.areaSelectLoaded = true;
	}

	public get extraValue(): string {
		if (this.currentServer) {
			return this.currentServer.extraValue;
		}

		return "";
	}

	private get isApplianceEnabled() {
		return get(this.featuresList, ["Devices", "Appliances"]);
	}

	private get isApplianceOnly() {
		return get(this.featuresList, ["Devices", "Appliances", "ApplianceOnly"]);
	}

	private get isApplianceSelected() {
		return !!this.selectedServer.applianceId;
	}

	private get isSlaEnabled(): boolean {
		return get(this.featuresList, ["Alarms", "SLAs"]);
	}

	private get readonly(): boolean {
		return this.selectedServer && !!this.selectedServer.readonly
	}

	private get isFailoverEnabled(): boolean {
		return get(this.featuresList, ["Devices", "AdvancedSetup", "ApplianceFailover"]) && (this.permissions.canFailOverAppliance || this.permissions.isSystemAdmin) && this.isApplianceEnabled;
	}

	private get isDeviceLevelFailoverEnabled(): boolean {
		return get(this.featuresList, ["Devices", "AdvancedSetup", "ApplianceFailover", "DeviceLevel"]) && (this.permissions.canFailOverAppliance || this.permissions.isSystemAdmin) && this.isApplianceEnabled;
	}

	private get isCustomFieldServersEnabled(): boolean {
		return !!get(this.featuresList, ["CustomFields", "Servers"]);
	}

	private get isEventECareEnabled(): boolean {
		return !!get(this.featuresList, ["Devices", "AdvancedSetup", "EventECare"]);
	}

	private get showHealthCheckSettings(): boolean {
		return !!get(this.featuresList, ["Devices", "AdvancedSetup", "HealthCheck"]);
	}

	public get canViewCustomFieldsPermission(): boolean {
		return this.permissions.canViewCustomFields;
	}

	private get doesServerSupportECare(): boolean {
		let currentServerType = this.serverTypes.find((st: ServerType) => st.serverTypeID === this.selectedServer.serverTypeID);

		return (currentServerType?.canEventECare && !!this.selectedServer?.applianceId) ?? false;
	}

	private get isEventHealthIntervalValueValid(): boolean {
		return this.selectedServer?.eventTestInterval == null || (this.selectedServer?.eventTestInterval >= 0 && this.selectedServer?.eventTestInterval <= this.maxIntervalValue);
	}

	private get isEventHealthTimeoutValueValid(): boolean {
		return this.selectedServer?.eventTestTimeout == null || (this.selectedServer?.eventTestTimeout >= 0 && this.selectedServer?.eventTestTimeout <= this.maxTimeoutValue);
	}

	private onCustomFieldsChanged(updatedFields: CustomFieldValues[]): void {
		this.selectedServer.customFields = [...updatedFields];
	}

	private areCustomFieldValuesInvalid(): boolean {
		// Only consider current values if feature enabled
		if (this.isCustomFieldServersEnabled) {
			return hasEmptyRequiredCustomFields(this.selectedServer.customFields, this.serverCustomFields) || hasInvalidCustomFieldValues(this.selectedServer.customFields, this.serverCustomFields);
		}

		return false;
	}

	private areEventHealthFieldsInvalid(): boolean {
		if (this.isEventECareEnabled) {
			return (this.selectedServer.eventTestInterval != null &&
				this.selectedServer.eventTestInterval < 0 ||
				this.selectedServer.eventTestInterval > this.maxIntervalValue) ||
				(this.selectedServer.eventTestTimeout != null &&
				this.selectedServer.eventTestTimeout < 0 ||
				this.selectedServer.eventTestTimeout > this.maxTimeoutValue);
		}

		return false;
	}

	private get currentServerBelongsToGroupSync(): boolean {
		return this.currentServer?.groupSyncId != null &&
			this.currentServer.groupSyncId > 0;
	}

	private async triggerEventTest(): Promise<void> {
		await this.handleTriggerEventTest(this);
	}

	private handleTriggerEventTest = debounce(async (component: InstanceType<typeof DeviceEdit>) => {
		await devicesApi.triggerDeviceTest(component.selectedServer.serverID);
	}, getDebouncePeriod());

	private integerFormatter(eventHealthValue: number): number {
		return enforceWholeNumber(eventHealthValue);
	}

	private heartbeatIntervalFormatter(eventHealthValue: number): number {
		let num = enforceWholeNumber(eventHealthValue);

		if (num < 0) {
			return 0;
		}

		if (num > this.maxIntervalValue) {
			return this.maxIntervalValue;
		}

		return num;
	}

	private numberFieldFormatting(event: object): void {
		numberFieldBadDataProtection(event, bannedNumberFieldKeys);
	}

	private get isTriggerEventTestDisabled(): boolean {
		return this.currentServer?.serverID == null ||
		this.currentServer.eventTestInterval == null ||
		this.currentServer.eventTestTimeout == null ||
		this.currentServer.eventTestInterval != this.selectedServer.eventTestInterval ||
		this.currentServer.eventTestTimeout != this.selectedServer.eventTestTimeout ||
		this.selectedServer.eventTestInterval.toString() == "" || this.selectedServer.eventTestTimeout.toString() == "";
	}

	private get isHealthCheckTypeAllServers(): boolean {
		return this.healthCheckType === HealthCheckTypeIds.AllServers;
	}

	private get isHealthCheckInputsEnabled(): boolean {
		if (!this.showHealthCheckSettings) {
			return false;
		}

		return this.selectedServer.heartbeatEnabled || (this.isHealthCheckTypeAllServers && this.selectedServer.heartbeatEnabled == null);
	}
}
